import type {
  CompanyProfile,
  CompanyProfileUpdate,
  LockDateState,
  LockDateUpdate,
  ModuleStatus,
  NumberSeriesInfo,
  PrintContext,
  PrintDocumentType,
  SecretName,
  SecretStatus,
  SettingGroup,
  SettingGroupState,
  SettingGroupValue,
} from '@sahab/contracts'
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { sessionKey } from '../auth/session'

export const settingsKeys = {
  all: ['settings'] as const,
  company: ['settings', 'company'] as const,
  group: (group: SettingGroup) => ['settings', 'group', group] as const,
  secrets: ['settings', 'secrets'] as const,
  printContexts: ['settings', 'print-context'] as const,
  printContext: (type: PrintDocumentType) => ['settings', 'print-context', type] as const,
  modules: ['settings', 'modules'] as const,
  numbering: ['settings', 'numbering'] as const,
  lockDate: ['settings', 'lock-date'] as const,
}

/** The legal identity printed on invoices, statements and reports (readable by every signed-in user). */
export const companyProfileQueryOptions = queryOptions({
  queryKey: settingsKeys.company,
  queryFn: ({ signal }) => api.get<CompanyProfile>('/settings/company', { signal }),
  staleTime: 5 * 60_000,
})

/** `PUT /settings/company`. A new display name renames the tenant, so the session is read again. */
export function useSaveCompanyProfile() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CompanyProfileUpdate) => api.put<CompanyProfile>('/settings/company', input),
    onSuccess: (profile) => {
      queryClient.setQueryData(settingsKeys.company, profile)
      void queryClient.invalidateQueries({ queryKey: settingsKeys.printContexts })
      void queryClient.invalidateQueries({ queryKey: sessionKey })
    },
  })
}

/** `GET /settings/:group`: a group never saved shows its defaults at version 1. */
export function settingGroupQueryOptions<G extends SettingGroup>(group: G) {
  return queryOptions({
    queryKey: settingsKeys.group(group),
    queryFn: ({ signal }) => api.get<SettingGroupState<G>>(`/settings/${group}`, { signal }),
    staleTime: 5 * 60_000,
  })
}

/**
 * The `documents` setting group (any signed-in user): the footer printed on invoices and other
 * documents, the terms, the payment details and the share-link defaults.
 */
export const documentSettingsQueryOptions = settingGroupQueryOptions('documents')

/** The `inventory` setting group (any signed-in user): the negative-stock policy. */
export const inventorySettingsQueryOptions = settingGroupQueryOptions('inventory')

/** The `payments` setting group (any signed-in user): the account each payment method starts on. */
export const paymentSettingsQueryOptions = settingGroupQueryOptions('payments')

/** `PATCH /settings/:group` with the version read; each field named replaces the stored one whole. */
export function useSaveSettingGroup<G extends SettingGroup>(group: G) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: Partial<SettingGroupValue<G>> & { version: number }) =>
      api.patch<SettingGroupState<G>>(`/settings/${group}`, input),
    onSuccess: (state) => {
      queryClient.setQueryData(settingsKeys.group(group), state)
      if (group === 'documents') void queryClient.invalidateQueries({ queryKey: settingsKeys.printContexts })
    },
  })
}

/**
 * `GET /settings/print-context?documentType=…`: everything a printed document of one type needs from
 * the settings, resolved (company, images, footer, terms, payment details).
 */
export function printContextQueryOptions(documentType: PrintDocumentType) {
  return queryOptions({
    queryKey: settingsKeys.printContext(documentType),
    queryFn: ({ signal }) =>
      api.get<PrintContext>('/settings/print-context', { query: { documentType }, signal }),
    staleTime: 60_000,
  })
}

/** `GET /settings/secrets`: whether each secret is set, when and by whom; never the value. */
export const secretsQueryOptions = queryOptions({
  queryKey: settingsKeys.secrets,
  queryFn: ({ signal }) => api.get<SecretStatus[]>('/settings/secrets', { signal }),
})

/** `PUT /settings/secrets/:name`: the value goes out once and is never kept or echoed. */
export function useSetSecret(name: SecretName) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (value: string) =>
      api.put<SecretStatus>(`/settings/secrets/${encodeURIComponent(name)}`, { value }),
    onSuccess: (status) => {
      queryClient.setQueryData<SecretStatus[]>(settingsKeys.secrets, (list) =>
        list ? list.map((s) => (s.name === name ? status : s)) : [status],
      )
      // Whether e-mail can be sent (`emailReady`) depends on the SMTP password being set.
      if (name === 'email.smtpPassword')
        void queryClient.invalidateQueries({ queryKey: settingsKeys.group('notifications') })
    },
  })
}

/** `DELETE /settings/secrets/:name`. */
export function useRemoveSecret(name: SecretName) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete<void>(`/settings/secrets/${encodeURIComponent(name)}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.secrets })
      if (name === 'email.smtpPassword')
        void queryClient.invalidateQueries({ queryKey: settingsKeys.group('notifications') })
    },
  })
}

/** `GET /settings/modules`: the modules the company is entitled to (read-only). */
export const modulesQueryOptions = queryOptions({
  queryKey: settingsKeys.modules,
  queryFn: ({ signal }) => api.get<ModuleStatus[]>('/settings/modules', { signal }),
  staleTime: 5 * 60_000,
})

/** `GET /numbering/series` (settings.manage): every document series, its format and next number. */
export const numberingQueryOptions = queryOptions({
  queryKey: settingsKeys.numbering,
  queryFn: ({ signal }) => api.get<NumberSeriesInfo[]>('/numbering/series', { signal }),
})

/** `GET /accounting/lock-date` (ledger.read). */
export const lockDateQueryOptions = queryOptions({
  queryKey: settingsKeys.lockDate,
  queryFn: ({ signal }) => api.get<LockDateState>('/accounting/lock-date', { signal }),
})

/** `PUT /accounting/lock-date` (fiscalYears.manage). */
export function useSaveLockDate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: LockDateUpdate) => api.put<LockDateState>('/accounting/lock-date', input),
    onSuccess: (state) => queryClient.setQueryData(settingsKeys.lockDate, state),
  })
}
